import { hexToHslTriplet } from './color';

export interface BoTheme {
  displayName?: string | null;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
}

/** Read the backoffice sub-key out of an org's `theme` jsonb column. */
export function readBoTheme(theme: unknown): BoTheme | null {
  if (!theme || typeof theme !== 'object') return null;
  const bo = (theme as Record<string, unknown>).backoffice;
  return bo && typeof bo === 'object' ? (bo as BoTheme) : null;
}

/**
 * Apply the backoffice brand to the DOM: primary/secondary HSL-var ramps,
 * favicon, and document title. Inline styles on <html> win over both `:root`
 * and `.dark`, so the brand color is one value across light/dark.
 *
 * ponytail: single brand color for both modes — no separate dark-mode swatch.
 * Add a `primaryColorDark` field + `.dark`-scoped write if per-mode brand
 * colors are ever needed.
 */
export function applyBoTheme(bo: BoTheme | null | undefined): void {
  const root = document.documentElement;

  if (bo?.primaryColor) {
    const triplet = hexToHslTriplet(bo.primaryColor);
    if (triplet) {
      root.style.setProperty('--primary', triplet);
      root.style.setProperty('--ring', triplet);
      root.style.setProperty('--sidebar-accent', triplet);
    }
  }

  if (bo?.secondaryColor) {
    const triplet = hexToHslTriplet(bo.secondaryColor);
    if (triplet) root.style.setProperty('--secondary', triplet);
  }

  if (bo?.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = bo.faviconUrl;
  }

  if (bo?.displayName) document.title = `${bo.displayName} · Backoffice`;
}
